import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import {
  BulkActivationResultItemDTO,
  BulkActivationUpdateResponseDTO,
  BulkResultItemDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogState } from '../../../component/dialog';
import { TabbedDialog, TabbedDialogTab } from '../../../component/dialog/TabbedDialog';
import { BenefitActivationDialogExtensionInterface } from '../../../extension/interface/benefit-activation-dialog.extension.interface';
import { useBenefitContext } from '../BenefitContext';
import { enqueueBulkResultSnackbar } from '../bulkActivation/bulkResultSnackbar';
import { getExtensionForBenefit } from '../index';
import { BulkActivationResultDialog } from './BulkActivationResultDialog';
import { BulkEditForm } from './BulkEditForm';
import { BulkErrorDialog } from './BulkErrorDialog';
import { DeactivateForm } from './DeactivateForm';
import { PauseForm } from './PauseForm';
import { SingleActivationResultDialog } from './SingleActivationResultDialog';
import { useBoundActivationComponent } from './useBoundActivationComponent';
import { BulkPauseFailedError, PauseMutationDTO } from './useBulkPauseActivationsMutation';
import { BulkUpdateFailedError } from './useBulkUpdateActivationsMutation';

interface Props {
  dialogState: DialogState;
  translationPrefix: string;
  isBulkMode?: boolean;
  terminationDate?: DateTime;
  onDeactivate?: (dto: UpdateBenefitActivationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onPause?: (dto: PauseMutationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onUpdate?: (dto: UpdateBenefitActivationDTO) => Promise<BulkActivationUpdateResponseDTO>;
  onSuccessReset?: () => void;
}

function hasResults(err: unknown): err is BulkUpdateFailedError | BulkPauseFailedError {
  return err instanceof BulkUpdateFailedError || err instanceof BulkPauseFailedError;
}

export const ActivationActionsDialog: React.FC<Props> = ({
  dialogState,
  translationPrefix,
  isBulkMode = false,
  terminationDate,
  onDeactivate,
  onPause,
  onUpdate,
  onSuccessReset,
}) => {
  const { t } = useTranslation('benefitsModule');
  const { benefit } = useBenefitContext();
  const { enqueueSnackbar } = useSnackbar();
  const [updateErrors, setUpdateErrors] = useState<BulkResultItemDTO[]>();
  const [resultDialogData, setResultDialogData] = useState<{ employees: BulkActivationResultItemDTO[]; variant: 'deactivate' | 'pause' }>();

  const benefitActivationDialogExtension = getExtensionForBenefit(benefit, BenefitActivationDialogExtensionInterface);

  const handleAction = useCallback(
    async (mutateFn: () => Promise<BulkActivationUpdateResponseDTO>, variant: 'deactivate' | 'pause' | 'update') => {
      setUpdateErrors(undefined);
      setResultDialogData(undefined);
      try {
        const response = await mutateFn();
        const successCount = enqueueBulkResultSnackbar(response.results, t, enqueueSnackbar, {
          empty: `${translationPrefix}.noEditable`,
          successFragment: `${translationPrefix}.${variant}Success`,
        });

        if (successCount) {
          onSuccessReset?.();
        }

        if (variant !== 'update') {
          const affectedEmployees = response.results.filter(r => r.status === (variant === 'deactivate' ? 'ADJUSTMENT' : 'BLOCKED'));
          if (affectedEmployees.length > 0) {
            setResultDialogData({ employees: affectedEmployees, variant });
            return;
          }
        }
      } catch (err) {
        if (hasResults(err)) {
          setUpdateErrors(err.results.filter(resultItem => resultItem.status === 'ERROR'));
          return;
        }
        // other errors already handled by mutation's onError
      }
      dialogState.handleClose();
    },
    [enqueueSnackbar, t, translationPrefix, onSuccessReset, dialogState],
  );

  const handlePause = useCallback(
    async (pauseDTO: PauseMutationDTO): Promise<void> => {
      if (!onPause) {
        return;
      }
      await handleAction(() => onPause(pauseDTO), 'pause');
    },
    [handleAction, onPause],
  );

  const handleDeactivate = useCallback(
    async (updateDTO: UpdateBenefitActivationDTO): Promise<void> => {
      if (!onDeactivate) {
        return;
      }
      await handleAction(() => onDeactivate(updateDTO), 'deactivate');
    },
    [handleAction, onDeactivate],
  );

  const handleUpdate = useCallback(
    async (updateDTO: UpdateBenefitActivationDTO): Promise<void> => {
      if (!onUpdate) {
        return;
      }
      await handleAction(() => onUpdate(updateDTO), 'update');
    },
    [handleAction, onUpdate],
  );

  const EditFormComponent = useBoundActivationComponent(BulkEditForm, benefit);

  const handleCloseError = useCallback(() => {
    setUpdateErrors(undefined);
  }, []);

  const handleCloseResult = useCallback(() => {
    setResultDialogData(undefined);
    dialogState.handleClose();
  }, [dialogState]);

  if (updateErrors) {
    return <BulkErrorDialog dialogState={{ isOpen: !!updateErrors, handleClose: handleCloseError }} updateErrors={updateErrors} />;
  }

  if (resultDialogData) {
    if (isBulkMode) {
      return (
        <BulkActivationResultDialog
          dialogState={{ isOpen: true, handleClose: handleCloseResult }}
          variant={resultDialogData.variant}
          employees={resultDialogData.employees}
        />
      );
    }
    return (
      <SingleActivationResultDialog
        dialogState={{ isOpen: true, handleClose: handleCloseResult }}
        variant={resultDialogData.variant}
        result={resultDialogData.employees[0]}
      />
    );
  }

  return (
    <TabbedDialog dialogState={dialogState} title={t(`${translationPrefix}.title`)} maxWidth="sm" fullWidth>
      {onDeactivate && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.deactivate`)}>
          <DeactivateForm onCancel={dialogState.handleClose} onUpdate={handleDeactivate} terminationDate={terminationDate} />
        </TabbedDialogTab>
      )}
      {onPause && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.pauseBenefit`)}>
          <PauseForm onUpdate={handlePause} terminationDate={terminationDate} />
        </TabbedDialogTab>
      )}
      {onUpdate && (
        <TabbedDialogTab label={t(`${translationPrefix}.tabs.changeOptions`)}>
          <EditFormComponent
            onCancel={dialogState.handleClose}
            onUpdate={handleUpdate}
            ChangeOptionsInfo={benefitActivationDialogExtension?.ChangeOptionsInfo}
          />
        </TabbedDialogTab>
      )}
    </TabbedDialog>
  );
};
